import dayjs from "dayjs";

import { colorsPalettes } from "@similarweb/styles";

import type { IChosenItems } from "app/@types/chosenItems";
import type { TranslateFunction } from "app/@types/I18nInterfaces";
import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import type { ETiers } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/Constants";
import {
    FIRST_AVAILABLE_DATE_FOR_DATA,
    FORMAT_DATA,
    getTiersMeta,
} from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/Constants";
import type {
    PayloadRankingEntity,
    Position,
    RankingDistributionCompareModeData,
    RankingDistributionPayload,
    RankingDistributionSingleModeData,
    RankingSingleModeEntity,
} from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/rankingDistribution.types";
import { RankingDistributionMetric } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/rankingDistribution.types";
import { RANKING_DISTRIBUTION_DICTIONARY } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/RankingDistributionTranslator";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";
import { swSettings } from "app/common/services/swSettings";
import { trimZeroPrefixFromNumberedString } from "app/filters/ngFilters";
import CountryService from "app/services/CountryService";
import type { IDurationData } from "app/services/DurationService";
import { BasicDurations, customRangeFormat } from "app/services/DurationService";

const getCurrentMonthNumberOfAppearanceFromPayload = (
    payloadRankingEntities: [PayloadRankingEntity, PayloadRankingEntity],
) => (payloadRankingEntities[1] ? payloadRankingEntities[1][1] : payloadRankingEntities[0][1]);

const getLastMonthNumberOfAppearanceFromPayload = (
    payloadRankingEntities: [PayloadRankingEntity, PayloadRankingEntity],
) => payloadRankingEntities[0][1];

const getRankingEntity = (
    payloadRankingEntities: [PayloadRankingEntity, PayloadRankingEntity],
    duration: string,
): RankingSingleModeEntity => {
    const isLast28Days = duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;

    const currentMonthIndex = isLast28Days ? 0 : 1;
    const previousMonthIndex = isLast28Days ? null : 0;
    return {
        currentMonth: payloadRankingEntities[currentMonthIndex][0],
        currentMonthNumberOfPositions: payloadRankingEntities[currentMonthIndex][1],
        previousMount: previousMonthIndex === null ? null : payloadRankingEntities[previousMonthIndex][0],
        previousMonthNumberOfPositions:
            previousMonthIndex !== null ? payloadRankingEntities[previousMonthIndex][1] : null,
    };
};

const getChangeSinceLastMonth = (currentMonthRankings: number, lastMonthRankings: number) => {
    if (currentMonthRankings === 0 && lastMonthRankings === 0) {
        return 0;
    }
    return (currentMonthRankings - lastMonthRankings) / lastMonthRankings;
};

const getWebsiteData = (rankingDistributionCompareModePayload: RankingDistributionPayload, website: string) => {
    const positions = Object.keys(rankingDistributionCompareModePayload);
    return positions.map((currentPosition) => {
        const currentMonthRankings = getCurrentMonthNumberOfAppearanceFromPayload(
            rankingDistributionCompareModePayload[currentPosition][website],
        );
        const lastMonthRankings = getLastMonthNumberOfAppearanceFromPayload(
            rankingDistributionCompareModePayload[currentPosition][website],
        );
        const totalKeywordsPerWebsite = getTotalKeywordsPerWebsite(rankingDistributionCompareModePayload);
        const currentWebsiteTotalKeywords = totalKeywordsPerWebsite.find(({ key }) => key === website).y;

        return {
            key: currentPosition,
            y: currentMonthRankings,
            change: getChangeSinceLastMonth(currentMonthRankings, lastMonthRankings),
            percentageFromTotal: (currentMonthRankings / currentWebsiteTotalKeywords) * 100,
        };
    });
};

const MAP_METRIC_TO_CHART_COLORS: Record<
    RankingDistributionMetric,
    (data?: RankingDistributionCompareModeData) => string[]
> = {
    [RankingDistributionMetric.ByPosition]: (data) => {
        return data.map(({ color }) => color);
    },
    [RankingDistributionMetric.ByWebsite]: () => {
        return [
            colorsPalettes.midnight["400"],
            colorsPalettes.blue["500"],
            colorsPalettes.blue["400"],
            colorsPalettes.blue["300"],
            colorsPalettes.blue["200"],
        ];
    },
};

const mergeData = (pageFilter: IChosenItemParams, chosenItems: IChosenItems) => {
    return pageFilter.map((item, index) => ({
        name: item.url, // Taking the URL as the name
        color: chosenItems[index].color, // Assigning color based on index, cycling if needed
    }));
};
const filterWebsiteWithData = (
    rankingDistributionCompareModePayload: RankingDistributionPayload,
    chosenItems: IChosenItems,
    pageFilter: string,
) => {
    const pageFilterJson = safeJsonParse(pageFilter, []);
    const siteList = mergeData(pageFilterJson, chosenItems);
    const positions = Object.keys(rankingDistributionCompareModePayload);

    return siteList.filter((site) => {
        return positions.some((position) =>
            Object.keys(rankingDistributionCompareModePayload[position]).includes(site.name),
        );
    });
};

const transformRankingDistributionCompareModeByPosition = (
    rankingDistributionCompareModePayload: RankingDistributionPayload,
    options?: FormatterOptions,
    pageFilter?: string,
): RankingDistributionCompareModeData => {
    const { chosenItems } = options || {};
    return filterWebsiteWithData(rankingDistributionCompareModePayload, chosenItems, pageFilter)
        .map(({ color, name }) => {
            return {
                color,
                name,
                isActive: true,
                data: getWebsiteData(rankingDistributionCompareModePayload, name),
            };
        })
        .filter(Boolean);
};

const getPositionData = (
    rankingDistributionCompareModePayload: RankingDistributionPayload,
    currentPosition: string,
    totalDataPerWebsite: {
        percentageFromTotal: number;
        change: number;
        y: number;
        lastMonthRankings: number;
        key: string;
    }[],
) => {
    const websites = Object.keys(rankingDistributionCompareModePayload[currentPosition]);
    return websites.map((website) => {
        const currentMonthRankings = getCurrentMonthNumberOfAppearanceFromPayload(
            rankingDistributionCompareModePayload[currentPosition][website],
        );
        const lastMonthRankings = getLastMonthNumberOfAppearanceFromPayload(
            rankingDistributionCompareModePayload[currentPosition][website],
        );
        const totalKeywordsForTheSite = totalDataPerWebsite.find(({ key }) => key === website).y;
        return {
            key: website,
            y: currentMonthRankings,
            change: getChangeSinceLastMonth(currentMonthRankings, lastMonthRankings),
            percentageFromTotal: (currentMonthRankings / totalKeywordsForTheSite) * 100,
        };
    });
};

const getTotalKeywordsPerWebsite = (rankingDistributionPayload: RankingDistributionPayload) => {
    const positions = Object.keys(rankingDistributionPayload);
    const websiteNames = Object.keys(rankingDistributionPayload[positions[0]]);
    const websiteData = websiteNames.map((websiteName) => {
        return {
            key: websiteName,
            y: 0,
            lastMonthRankings: 0,
            percentageFromTotal: 100,
        };
    });
    const totalKeywordsPerWebsiteWithoutChange = positions.reduce((acc, positionData, currentIndex) => {
        const websites = Object.keys(rankingDistributionPayload[positions[currentIndex]]);
        return websites.map((website) => {
            const websiteRankings = getCurrentMonthNumberOfAppearanceFromPayload(
                rankingDistributionPayload[positionData][website],
            );
            const lastMonthRankings = getLastMonthNumberOfAppearanceFromPayload(
                rankingDistributionPayload[positionData][website],
            );
            const index = acc.findIndex(({ key }) => key === website);
            return {
                ...acc[index],
                y: acc[index].y + websiteRankings,
                lastMonthRankings: acc[index].lastMonthRankings + lastMonthRankings,
            };
        });
    }, websiteData);
    return totalKeywordsPerWebsiteWithoutChange.map((currentSiteData) => {
        return {
            ...currentSiteData,
            change: getChangeSinceLastMonth(currentSiteData.y, currentSiteData.lastMonthRankings),
        };
    });
};

const transformRankingDistributionMetricCompareModeByWebsite = (
    rankingDistributionPayload: RankingDistributionPayload,
    { translate }: { translate: TranslateFunction },
) => {
    const totalDataPerWebsite = getTotalKeywordsPerWebsite(rankingDistributionPayload);
    const positions = Object.keys(rankingDistributionPayload);
    const colors = MAP_METRIC_TO_CHART_COLORS[RankingDistributionMetric.ByWebsite]();
    const positionsData = positions.map((currentPosition, index) => {
        return {
            name: trimZeroPrefixFromNumberedString(currentPosition, "-"),
            color: colors[index],
            isActive: true,
            data: getPositionData(rankingDistributionPayload, currentPosition, totalDataPerWebsite),
        };
    });
    return [
        ...positionsData,
        {
            name: translate(RANKING_DISTRIBUTION_DICTIONARY.TOTAL),
            color: colorsPalettes.carbon["300"],
            isActive: false,
            data: totalDataPerWebsite,
        },
    ];
};

export const transformRankingDistributionPayloadToRankingDistributionData = (
    rankingDistributionPayload: RankingDistributionPayload,
    duration: string,
): RankingDistributionSingleModeData => {
    const positions = Object.keys(rankingDistributionPayload);
    const website = Object.keys(rankingDistributionPayload[positions[0]])[0];
    return positions.reduce<RankingDistributionSingleModeData>(
        (rankingDistributionDataAccumulator, currentPosition) => {
            return {
                ...rankingDistributionDataAccumulator,
                [currentPosition]: getRankingEntity(rankingDistributionPayload[currentPosition][website], duration),
            };
        },
        {} as RankingDistributionSingleModeData,
    );
};

const PERCENTAGE = "%";

const getMetricCompareBarValueText = ({ currentMonth, total }) =>
    `${Math.floor((currentMonth / total) * 100)}${PERCENTAGE}`;

export const getMetricsCompareBarItems = (rankingDistributionData: RankingDistributionSingleModeData) => {
    if (!rankingDistributionData) {
        return [];
    }

    const positions = Object.keys(rankingDistributionData) as Position[];

    const totalRankings = positions.reduce((rankingsAccumulator, position) => {
        return rankingsAccumulator + (rankingDistributionData[position]?.currentMonthNumberOfPositions ?? 0);
    }, 0);

    const tiersMeta = getTiersMeta();
    const bars = positions.map((position, index) => {
        const tier = tiersMeta[index as keyof typeof tiersMeta];
        return {
            color: tier?.color,
            displayName: tier?.text ?? trimZeroPrefixFromNumberedString(positions[index], "-"),
            name: tier?.text ?? trimZeroPrefixFromNumberedString(positions[index], "-"),
            width: getMetricCompareBarValueText({
                currentMonth: rankingDistributionData[position].currentMonthNumberOfPositions,
                total: totalRankings,
            }).slice(0, -1),
            valueText: getMetricCompareBarValueText({
                currentMonth: rankingDistributionData[position].currentMonthNumberOfPositions,
                total: totalRankings,
            }),
        };
    });
    return [{ bars }];
};

export const getBoxSubtitlesFilters = ({
    durationData,
    webSource,
    country,
}: {
    durationData: IDurationData;
    webSource: string;
    country: string;
}) => {
    return [
        {
            filter: "date",
            value: {
                from: durationData.raw.from.valueOf(),
                to: durationData.raw.to.valueOf(),
            },
        },
        {
            filter: "webSource",
            value: webSource,
        },
        {
            filter: "country",
            countryCode: country,
            value: CountryService.getCountryById(country).text,
        },
    ];
};

// these are used as labels for the x axis of the chart in single mode
const getCategories = (data: Record<string, any[]>) => {
    return Object.values(data)[0].map((v) => v[0]);
};

const getMaxColumnValue = (data, dataKeys, filter) => {
    let max = 0;
    let counter = 0;
    const filteredKeys = dataKeys.filter((key) => filter.includes(key));
    for (let i = 0; i < data[filteredKeys[0]].length; i++) {
        counter = 0;
        for (let j = 0; j < filteredKeys.length; j++) {
            counter += data[filteredKeys[j]][i][1];
        }
        max = Math.max(max, counter);
    }
    return max;
};

const buildSeriesForChart = (data = {}, baseSeries = [], isFiltered = false) => {
    const dataKeys = Object.keys(data);
    if (!dataKeys?.length) {
        return undefined;
    }

    let maxSeriesValue;
    if (isFiltered) {
        maxSeriesValue = getMaxColumnValue(
            data,
            dataKeys,
            baseSeries.map((s) => s.key),
        );
    }
    // divide by maxSeriesValue in order to normalize the columns relative to the largest column.
    baseSeries.forEach((seriesItem) => {
        seriesItem.data = data[seriesItem.key].map((v) => {
            const dataPoint = v[1];
            return dataPoint !== undefined ? (isFiltered ? dataPoint / maxSeriesValue : dataPoint) : 0;
        });
    });
    baseSeries.filter((seriesItem) => seriesItem.data.length);
    return baseSeries.length ? baseSeries : undefined;
};
// range =< (maxDate - minDate) ? range : maxDate
const validateRange = (range) => {
    const maxDate = swSettings.components.RankingDistribution.endDate;
    const minDate = dayjs.utc(FIRST_AVAILABLE_DATE_FOR_DATA, FORMAT_DATA);
    const diffIncludingEndDate = maxDate.diff(minDate, "months") + 1;

    return range <= diffIncludingEndDate ? range : diffIncludingEndDate;
};

const selectedRangeDataExists = (chosenDate: string, range = 12) => {
    const chosenDateTimestamp = dayjs.utc(chosenDate, customRangeFormat);
    const earliestStartDateTimestamp = dayjs.utc(FIRST_AVAILABLE_DATE_FOR_DATA, FORMAT_DATA);
    const diff = chosenDateTimestamp.diff(earliestStartDateTimestamp, "months");
    return diff + 1 >= range;
};

// set the plotband around the chosen date on the x axis
const getPlotBandRange = (categories, duration) => {
    const indexOfDuration = categories.findIndex((c) =>
        dayjs.utc(c, FORMAT_DATA).isSame(dayjs.utc(duration, customRangeFormat), "month"),
    );
    return {
        from: indexOfDuration - 0.5,
        to: indexOfDuration + 0.5,
    };
};

// methods for graph tooltips
const calculateChange = (currentMonthsValue, previousMonthsValue, percentageFilter) => {
    if (!previousMonthsValue || !currentMonthsValue) {
        return "N/A";
    }
    const difference = currentMonthsValue - previousMonthsValue;
    if (difference === 0) {
        return difference;
    }
    return percentageFilter(difference / previousMonthsValue);
};

export const percentsFilter = (val, fractionDigit = 2) =>
    (Math.round(val * 100) / 100).toFixed(fractionDigit) + PERCENTAGE;

interface FormatterOptions {
    translate?: TranslateFunction;
    chosenItems?: IChosenItems;
}

export const MAP_METRIC_TO_FORMATTING_FUNCTION: Record<
    RankingDistributionMetric,
    (
        rankingDistributionPayload: RankingDistributionPayload,
        options?: FormatterOptions,
        pageFilter?: string,
    ) => RankingDistributionCompareModeData
> = {
    [RankingDistributionMetric.ByPosition]: transformRankingDistributionCompareModeByPosition,
    [RankingDistributionMetric.ByWebsite]: transformRankingDistributionMetricCompareModeByWebsite,
};

export function filterDataAccordingToRankingFilters<T extends { id: string }>(
    data: Array<T>,
    displayedTiers: ETiers[],
): Array<T> {
    const tiersMeta = getTiersMeta();
    return data.filter(({ id }) => {
        return (
            displayedTiers.find((displayedTier) => {
                return tiersMeta[displayedTier].id === id;
            }) !== undefined
        );
    });
}

const MAX_SUPPORTED_POSITION = 100;

export const calcDomainsPositions = (data: Array<{ Site: string; Position?: number }>) => {
    return data.reduce<{ domain: string | null; value: number }>(
        (res, entry) => {
            if (entry.Position !== undefined && entry.Position <= res.value) {
                res.domain = entry.Site;
                res.value = entry.Position;
            }
            return res;
        },
        { value: MAX_SUPPORTED_POSITION, domain: null },
    );
};
